import React, { useEffect, useState, useMemo } from "react";
import { apiFetch } from "../api";
import HumiditeBancSection from "./HumiditeBancSection";
import DensiteBancSection from "./DensiteBancSection";
import LongueurBancSection from "./LongueurBancSection";

import { useI18n } from "../i18n/I18nContext";

// ── helpers ───────────────────────────────────────────────────────────────────
const toF = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = parseFloat(String(v).replace(",", "."));
  return isNaN(n) ? 0 : n;
};

const pctConsomme = (lot) => {
  const total   = toF(lot.NombreDePlacage);
  const restant = toF(lot.NombreDePlacageRestant);
  if (total <= 0) return 0;
  return Math.round(((total - restant) / total) * 100);
};

const couleurPct = (pct) => {
  if (pct >= 75) return COLORS.red;
  if (pct >= 40) return COLORS.orange;
  return COLORS.green;
};

function parseDate(str) {
  if (!str) return null;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split("/");
    return new Date(`${y}-${m}-${d}`);
  }
  return new Date(str);
}

function periodStart(p) {
  const now = new Date();
  if (p === "day")   return new Date(now - 86400000);
  if (p === "week")  return new Date(now - 7 * 86400000);
  if (p === "month") return new Date(now - 30 * 86400000);
  return new Date(0);
}

const PERIODES = [
  { key: "all",   label: "Tout"       },
  { key: "month", label: "Mois"       },
  { key: "week",  label: "Semaine"    },
  { key: "day",   label: "Jour"       },
];

const COLORS = {
  green:  "#4ade80",
  red:    "#f87171",
  yellow: "#E6FF3E",
  blue:   "#60a5fa",
  orange: "#fb923c",
  purple: "#c084fc",
  gray:   "#64748b",
};

// ════════════════════════════════════════════════════════════════════════════
// KpiCard — inspiré DashboardQualite
// ════════════════════════════════════════════════════════════════════════════
function KpiCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ ...S.kpiCard, borderTopColor: color }}>
      <div style={S.kpiIcon}>{icon}</div>
      <div style={{ ...S.kpiValue, color }}>{value ?? "—"}</div>
      <div style={S.kpiLabel}>{label}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// LotCard
// ════════════════════════════════════════════════════════════════════════════
function LotCard({ rank, lot, couleur, showPct = false, pctPN }) {
  const pct     = pctConsomme(lot);
  const total   = toF(lot.NombreDePlacage);
  const restant = toF(lot.NombreDePlacageRestant);

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "9px 12px", borderRadius: 8,
      background: "rgba(255,255,255,0.02)",
      border: "1px solid #0f2a3d",
      borderLeft: `3px solid ${couleur}`,
      transition: "background 0.15s",
    }}>
      <span style={{
        color: COLORS.gray, fontSize: 10, fontWeight: 800,
        background: "rgba(255,255,255,0.04)",
        borderRadius: 4, padding: "1px 5px", minWidth: 20, textAlign: "center",
      }}>
        {rank}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 600, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {lot.PN}
        </div>
        <div style={{ fontSize: 10, color: COLORS.gray, marginTop: 3, display: "flex", gap: 5 }}>
          <span>{lot.Fournisseur}</span>
          <span style={{ opacity: 0.3 }}>·</span>
          <span style={{ color: COLORS.blue, fontWeight: 600 }}>{lot.Essence}</span>
          <span style={{ opacity: 0.3 }}>·</span>
          <span>{total} plac.</span>
        </div>
      </div>
      {showPct ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, minWidth: 100 }}>
          {/* % stock consommé (basé sur restant) */}
          <span style={{ fontSize: 13, fontWeight: 900, color: couleurPct(pct) }}>{pct}%</span>
          <div style={{ width: 76, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: couleurPct(pct), borderRadius: 999, transition: "width 0.4s ease" }} />
          </div>

          {/* % PN empilement (basé sur prélèvements réels) */}
          {pctPN !== undefined && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, marginTop: 4 }}>
              <span style={{ fontSize: 9, color: "#94a3b8", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>PLYPN empil.</span>
              <span style={{ fontSize: 11, fontWeight: 800, color: couleurPct(pctPN) }}>{pctPN}%</span>
              <div style={{ width: 76, height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.min(100, pctPN)}%`, background: couleurPct(pctPN), borderRadius: 999, transition: "width 0.4s ease" }} />
              </div>
            </div>
          )}

          <span style={{ fontSize: 9, color: COLORS.gray, marginTop: 2 }}>{restant} restants</span>
        </div>
      ) : (
        <span style={{ fontSize: 10, color: COLORS.gray, whiteSpace: "nowrap" }}>{restant} restants</span>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ColonneLots
// ════════════════════════════════════════════════════════════════════════════
function ColonneLots({ titre, icone, lots, couleur, showPct = false }) {
  return (
    <div style={S.card}>
      <div style={{ ...S.cardTitle, justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>{icone}</span>
          <span style={{ color: couleur }}>{titre}</span>
        </div>
        <span style={{
          background: `${couleur}18`, color: couleur,
          border: `1px solid ${couleur}33`,
          borderRadius: 99, padding: "2px 10px",
          fontSize: 12, fontWeight: 900, fontFamily: "monospace",
        }}>
          {lots.length}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5, maxHeight: 320, overflowY: "auto" }}>
        {lots.length === 0 ? (
          <div style={S.emptyState}>📭 Aucun lot</div>
        ) : (
          lots.map((lot, i) => (
            <LotCard
              key={lot["Num de lot"] || i}
              rank={i + 1}
              lot={lot}
              couleur={couleur}
              showPct={showPct}
              pctPN={lot._pctPN}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DashboardLogistique
// ════════════════════════════════════════════════════════════════════════════
export default function DashboardLogistique() {
  const { t, ui } = useI18n();


  const [lots,         setLots]         = useState([]);
  const [prelevements, setPrelevements] = useState([]);
  const [pliesKO,      setPliesKO]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [filterFourn,  setFilterFourn]  = useState("Tous");
  const [filterEss,    setFilterEss]    = useState("Toutes");
  const [periode,      setPeriode]      = useState("all");

  // ── Chargement ────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [r1, r2, r3] = await Promise.all([
          apiFetch("/api/list"),
          apiFetch("/api/production/prelevement/list"),
          apiFetch("/api/pliesKO/list"),
        ]);
        const [d1, d2, d3] = await Promise.all([r1.json(), r2.json(), r3.json()]);
        setLots(Array.isArray(d1) ? d1 : []);
        setPrelevements(Array.isArray(d2) ? d2 : []);
        setPliesKO(Array.isArray(d3) ? d3 : []);
      } catch {
        setError("Erreur chargement données");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Filtres ───────────────────────────────────────────────────────────────
  const fournisseurs = useMemo(() =>
    ["Tous", ...Array.from(new Set(lots.map(l => l.Fournisseur).filter(Boolean))).sort()], [lots]);
  const essences = useMemo(() =>
    ["Toutes", ...Array.from(new Set(lots.map(l => l.Essence).filter(Boolean))).sort()], [lots]);

  const lotsFiltres = useMemo(() =>
    lots.filter(l => {
      const mF = filterFourn === "Tous"   || l.Fournisseur === filterFourn;
      const mE = filterEss   === "Toutes" || l.Essence     === filterEss;
      return mF && mE;
    }), [lots, filterFourn, filterEss]);

  // ── Catégorisation ────────────────────────────────────────────────────────
  const lotsFinis   = useMemo(() => lotsFiltres.filter(l => l.Statut === "FINI"),    [lotsFiltres]);
  const lotsNeufs   = useMemo(() => lotsFiltres.filter(l => l.Statut === "NEUF"),    [lotsFiltres]);

  const lotsEncours = useMemo(() => {
    // Calcul des prélèvements totaux par PN pour "PLYPN empilement"
    const placagesParPN = prelevements.reduce((acc, p) => {
      const pn = p.PN;
      if (!pn) return acc;
      acc[pn] = (acc[pn] || 0) + toF(p["QuantitePlacage"]);
      return acc;
    }, {});

    return lotsFiltres
      .filter(l => l.Statut === "EN COURS")
      .map(l => {
        const total = toF(l.NombreDePlacage);
        const consommePN = placagesParPN[l.PN] || 0;
        const _pctPN = total > 0 ? Math.round((consommePN / total) * 100) : 0;

        return {
          ...l,
          _pct: pctConsomme(l),
          _pctPN
        };
      })
      .sort((a, b) => b._pct - a._pct);
  }, [lotsFiltres, prelevements]);

  // ── Période ───────────────────────────────────────────────────────────────
  const start = useMemo(() => periodStart(periode), [periode]);
  const prelevPeriode = useMemo(() =>
    prelevements.filter(p => { const d = parseDate(p.time); return d && d >= start; }),
  [prelevements, start]);

  // ── Métriques ─────────────────────────────────────────────────────────────
  const metriques = useMemo(() => {
    const heuresPeriode = periode === "day" ? 24 : periode === "week" ? 168 : periode === "month" ? 720 : null;

    const totalPlacagesConsos = prelevPeriode.reduce((s, p) => s + toF(p["QuantitePlacage"]), 0);

    let vitesse = null;
    if (totalPlacagesConsos > 0) {
      if (heuresPeriode) {
        vitesse = Math.round((totalPlacagesConsos / heuresPeriode) * 100) / 100;
      } else {
        const ts = prelevPeriode.map(p => parseDate(p.time)).filter(Boolean).map(d => d.getTime());
        if (ts.length >= 2) {
          const dur = (Math.max(...ts) - Math.min(...ts)) / 3600000;
          if (dur > 0) vitesse = Math.round((totalPlacagesConsos / dur) * 100) / 100;
        }
      }
    }

    const totalDisponible = lots.filter(l => l.Statut !== "FINI").reduce((s, l) => s + toF(l.NombreDePlacage), 0);
    const tauxConso = totalDisponible > 0 ? Math.round((totalPlacagesConsos / totalDisponible) * 100) : null;

    const tousRebuts = pliesKO.filter(p => { const d = parseDate(p.time); return d && d >= start; });
    const totalNOK  = tousRebuts.reduce((s, p) => s + toF(p["PliesNOK"]), 0);
    const totalOK   = tousRebuts.reduce((s, p) => s + toF(p["PliesSimple"]) + toF(p["PliesDouble"]) + toF(p["PliesTriple"]), 0);
    const totalPlis = totalNOK + totalOK;
    const tauxRebut = totalPlis > 0 ? Math.round((totalNOK / totalPlis) * 1000) / 10 : null;

    const placagesRestants = lots.filter(l => l.Statut !== "FINI").reduce((s, l) => s + toF(l.NombreDePlacageRestant), 0);
    const profondeur = vitesse && vitesse > 0 ? Math.round(placagesRestants / vitesse) : null;
    const profondeurLabel = profondeur !== null
      ? profondeur >= 48 ? `${Math.round(profondeur / 24)} jours` : `${profondeur} h`
      : null;

    const pnsActifs = new Set(prelevements.map(p => p.PN));
    const now = new Date();
    const lotsDormants = lots
      .filter(l => l.Statut === "NEUF")
      .filter(l => !pnsActifs.has(l.PN))
      .filter(l => { const d = parseDate(l.Date); return d && (now - d) / 86400000 > 30; }).length;

    return {
      vitesse, totalPlacagesConsos: Math.round(totalPlacagesConsos), heuresPeriode,
      tauxConso, tauxRebut, totalNOK: Math.round(totalNOK), totalPlis: Math.round(totalPlis),
      profondeur, profondeurLabel, placagesRestants: Math.round(placagesRestants), lotsDormants,
    };
  }, [prelevPeriode, prelevements, pliesKO, lots, periode, start]);

  // ── Loading / Error ───────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...S.page, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
        <div style={S.spinner} />
        <div style={{ color: "#94a3b8", fontSize: 14 }}>Chargement des données logistiques…</div>
      </div>
    </div>
  );
  if (error) return (
    <div style={{ ...S.page, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ color: COLORS.red, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 12, padding: "20px 28px", fontSize: 14 }}>
        ❌ {error}
      </div>
    </div>
  );

  const rebutColor = metriques.tauxRebut === null ? COLORS.gray
    : metriques.tauxRebut > 5 ? COLORS.red
    : metriques.tauxRebut > 2 ? COLORS.orange
    : COLORS.green;

  const profColor = metriques.profondeur === null ? COLORS.gray
    : metriques.profondeur < 24 ? COLORS.red
    : metriques.profondeur < 72 ? COLORS.orange
    : COLORS.green;

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* ══ HEADER ═════════════════════════════════════════════════════ */}
        <div style={S.header}>
          <div>
            <div style={S.title}>{ui("Logistique")}</div>
            <div style={S.subtitle}>Suivi des stocks et indicateurs de consommation bois</div>
          </div>
          {/* Sélecteur période */}
          <div style={S.periodBar}>
            {PERIODES.map(p => (
              <button key={p.key}
                style={{ ...S.periodBtn, ...(periode === p.key ? S.periodBtnActive : {}) }}
                onClick={() => setPeriode(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* ══ FILTRES ════════════════════════════════════════════════════ */}
        <div style={S.filterBar}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={S.filterLabel}>Fournisseur</span>
            {fournisseurs.map(f => (
              <button key={f}
                style={{ ...S.chip, ...(filterFourn === f ? S.chipActive : {}) }}
                onClick={() => setFilterFourn(f)}>
                {f}
              </button>
            ))}
          </div>
          <div style={S.filterDivider} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={S.filterLabel}>Essence</span>
            {essences.map(e => (
              <button key={e}
                style={{ ...S.chip, ...(filterEss === e ? { ...S.chipActive, borderColor: COLORS.blue + "88", color: COLORS.blue, background: COLORS.blue + "14" } : {}) }}
                onClick={() => setFilterEss(e)}>
                {e}
              </button>
            ))}
          </div>
        </div>

        {/* ══ KPIs ═══════════════════════════════════════════════════════ */}
        <div style={S.kpiRow}>
          <KpiCard icon="⚡" label="Vitesse consommation"
            value={metriques.vitesse !== null ? `${metriques.vitesse} plac/h` : "—"}
            sub={metriques.heuresPeriode
              ? `${metriques.totalPlacagesConsos} plac. / ${metriques.heuresPeriode}h`
              : `${metriques.totalPlacagesConsos} plac. total`}
            color={COLORS.orange}
          />
          <KpiCard icon="📊" label="Taux de consommation"
            value={metriques.tauxConso !== null ? `${metriques.tauxConso}%` : "—"}
            sub="consommé / stock total"
            color={COLORS.blue}
          />
          <KpiCard icon="🗑️" label="Taux de rebut"
            value={metriques.tauxRebut !== null ? `${metriques.tauxRebut}%` : "—"}
            sub={`${metriques.totalNOK} NOK · ${metriques.totalPlis} plis`}
            color={rebutColor}
          />
          <KpiCard icon="📦" label="Profondeur de stock"
            value={metriques.profondeurLabel ?? "—"}
            sub={`${metriques.placagesRestants.toLocaleString("fr-FR")} plac. restants`}
            color={profColor}
          />
          <KpiCard icon="💤" label="Stock dormant"
            value={`${metriques.lotsDormants} lot${metriques.lotsDormants !== 1 ? "s" : ""}`}
            sub="NEUF · jamais prélevé · >30j"
            color={metriques.lotsDormants > 0 ? COLORS.orange : COLORS.green}
          />
          <KpiCard icon="🔄" label="Lots en cours"
            value={lotsEncours.length}
            sub={`${lotsNeufs.length} neufs · ${lotsFinis.length} finis`}
            color={COLORS.yellow}
          />
        </div>

        {/* ══ 3 COLONNES LOTS ════════════════════════════════════════════ */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr", gap: 16 }}>
          <ColonneLots titre="Lots Finis"             icone="✅" lots={lotsFinis}   couleur={COLORS.gray}   />
          <ColonneLots titre="Lots Neufs"             icone="🆕" lots={lotsNeufs}   couleur={COLORS.green}  />
          <ColonneLots titre="Lots Ouverts / Encours" icone="🔄" lots={lotsEncours} couleur={COLORS.yellow} showPct />
        </div>

        {/* ══ HUMIDITÉ BANC DE TEST ═════════════════════════════════════ */}
        <HumiditeBancSection
          lots={lots}
          filterEss={filterEss}
          filterFourn={filterFourn}
        />

        <DensiteBancSection
          lots={lots}
          filterEss={filterEss}
          filterFourn={filterFourn}
        />

        <LongueurBancSection
          lots={lots}
          filterEss={filterEss}
          filterFourn={filterFourn}
        />

      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:       { minHeight: "100vh", background: "linear-gradient(160deg,#041e2e 0%,#063549 60%,#052a3e 100%)", color: "#e2e8f0", fontFamily: "'Inter',sans-serif", padding: "24px 28px" },
  wrap:       { maxWidth: 1600, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 },
  header:     { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 },
  title:      { fontSize: 28, fontWeight: 900, background: "linear-gradient(90deg,#fff 0%,#E6FF3E 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" },
  subtitle:   { fontSize: 13, color: "#94a3b8", marginTop: 4 },
  periodBar:  { display: "flex", gap: 4, background: "rgba(255,255,255,0.04)", border: "1px solid #1e3a50", borderRadius: 8, padding: 4 },
  periodBtn:  { padding: "6px 14px", borderRadius: 6, border: "none", background: "transparent", color: "#94a3b8", cursor: "pointer", fontWeight: 700, fontSize: 12, transition: "all 0.15s" },
  periodBtnActive: { background: "#E6FF3E", color: "#000" },
  filterBar:  { display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", padding: "12px 18px", borderRadius: 12, background: "rgba(255,255,255,0.025)", border: "1px solid #1e3a50" },
  filterLabel:{ fontSize: 10, color: "#94a3b8", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", minWidth: 60 },
  filterDivider: { width: 1, height: 22, background: "#1e3a50", flexShrink: 0 },
  chip:       { padding: "4px 12px", borderRadius: 20, border: "1px solid #1e3a50", background: "transparent", color: "#64748b", cursor: "pointer", fontSize: 11, fontWeight: 600, transition: "all 0.15s", outline: "none" },
  chipActive: { borderColor: "#E6FF3E88", color: "#E6FF3E", background: "#E6FF3E14" },
  kpiRow:     { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 },
  kpiCard:    { background: "rgba(255,255,255,0.03)", border: "1px solid #1e3a50", borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, borderTopWidth: 3, backdropFilter: "blur(8px)" },
  kpiIcon:    { fontSize: 24 },
  kpiValue:   { fontSize: 28, fontWeight: 900, letterSpacing: "-0.02em" },
  kpiLabel:   { fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "center" },
  kpiSub:     { fontSize: 11, color: "#64748b", textAlign: "center" },
  card:       { background: "rgba(255,255,255,0.025)", border: "1px solid #1e3a50", borderRadius: 14, padding: "18px 20px", backdropFilter: "blur(8px)" },
  cardTitle:  { fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 },
  emptyState: { textAlign: "center", padding: "32px 0", color: "#64748b", fontSize: 13 },
  spinner:    { width: 36, height: 36, border: "3px solid #1e3a50", borderTop: "3px solid #E6FF3E", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
};